import React, { useEffect, useMemo } from 'react';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { fetchAccountSummaryByOrg } from 'js/actions/metrics';
import styles from './ResourceTileGrid.module.scss';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_ORG_MEMBERS_REQUEST, GET_RESOURCE_COUNT_REQUEST } from 'js/constants/actionTypes';
import useResourceCount from 'new/hooks/adapters/useResourceCount';
import { ResourceTile } from './ResourceTile';
import { fetchOrgMembers } from 'js/actions/orgs';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { useOrgMembers } from 'new/hooks/adapters/useOrgMembers';

interface ResourceTileGridProps {}

const generateResourceTileMap = (summary: any, totalActiveMembers = 0, totalDataProducts = 0) => {
  return {
    sources: {
      title: 'Data Sources',
      subtitle: "Connected integrations you've created",
      borderColor: 'blue',
      value: summary?.data_sources?.total || 0,
    },
    destinations: {
      title: 'Data Destinations',
      subtitle: "Delivery endpoints you've set up",
      borderColor: 'green',
      value: summary?.data_sinks?.total || 0,
    },
    members: {
      title: 'Active Members',
      subtitle: 'Collaborators in your organization',
      value: totalActiveMembers || 0,
      borderColor: 'orange',
    },
    nexsets: {
      title: 'Nexsets Created',
      subtitle: "Total Nexsets you've created",
      value: totalDataProducts || 0,
      borderColor: 'purple',
    },
  };
};

export const ResourceTileGrid = (props: ResourceTileGridProps) => {
  const dispatch = useAppDispatch();
  const org = useCurrentOrg();
  const { currentMode, ...summary } = useResourceCount('user');
  const members = useOrgMembers({ onlyValid: true });
  const totalActiveMembers = members?.length;
  const totalDataProducts =
    summary?.data_sets?.derived?.total + summary?.data_sets?.detected?.total;
  const isLoading = useIsLoading(GET_RESOURCE_COUNT_REQUEST, GET_ORG_MEMBERS_REQUEST);

  const RESOURCE_TILE_MAP = useMemo(
    () => generateResourceTileMap(summary, totalActiveMembers, totalDataProducts),
    [summary, totalActiveMembers, totalDataProducts],
  );

  useEffect(() => {
    dispatch(fetchAccountSummaryByOrg(currentMode));
  }, [dispatch, currentMode]);

  useEffect(() => {
    if (org?.id) {
      dispatch(fetchOrgMembers(org.id));
    }
  }, [dispatch, org?.id]);

  return (
    <div className={styles.grid}>
      {Object.entries(RESOURCE_TILE_MAP).map(([type, { title, subtitle, value, borderColor }]) => (
        <ResourceTile
          key={type}
          type={type}
          title={title}
          subtitle={subtitle}
          value={value}
          isLoading={isLoading}
          borderColor={borderColor}
        />
      ))}
    </div>
  );
};
